import * as React from "react"
import { createPortal } from "react-dom"
import { CheckIcon, ChevronDownIcon, Search } from "lucide-react"
import { cn } from "@/lib/utils"

interface SearchableSelectProps {
  value: string
  onValueChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Icon element rendered before the selected label in the trigger */
  icon?: React.ReactNode
}

function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Sélectionner...",
  className,
  disabled = false,
  icon,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [position, setPosition] = React.useState({ top: 0, left: 0, width: 0 })

  const selectedLabel = React.useMemo(
    () => options.find((o) => o.value === value)?.label,
    [options, value]
  )

  const filtered = React.useMemo(() => {
    if (!search) return options
    const term = search.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(term))
  }, [options, search])

  // Position the dropdown below the trigger
  const updatePosition = React.useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setPosition((prev) => {
      const top = rect.bottom + 4
      if (prev.top === top && prev.left === rect.left && prev.width === rect.width) return prev
      return { top, left: rect.left, width: rect.width }
    })
  }, [])

  // Open handler
  const handleOpen = () => {
    if (disabled) return
    updatePosition()
    setOpen(true)
    setSearch("")
  }

  // Single effect: click-outside, escape, focus, scroll/resize reposition
  React.useEffect(() => {
    if (!open) return

    requestAnimationFrame(() => inputRef.current?.focus())

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (dropdownRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }

    document.addEventListener("mousedown", onMouseDown)
    document.addEventListener("keydown", onKeyDown)
    window.addEventListener("scroll", updatePosition, true)
    window.addEventListener("resize", updatePosition)
    return () => {
      document.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("scroll", updatePosition, true)
      window.removeEventListener("resize", updatePosition)
    }
  }, [open, updatePosition])

  const handleSelect = (optionValue: string) => {
    onValueChange(optionValue)
    setOpen(false)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : handleOpen())}
        disabled={disabled}
        className={cn(
          "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-9",
          className
        )}
        data-placeholder={!selectedLabel ? "" : undefined}
      >
        <span className="flex items-center gap-2 truncate">
          {icon}
          {selectedLabel || (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </span>
        <ChevronDownIcon className="size-4 opacity-50 shrink-0" />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 z-50 rounded-md border shadow-md"
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: Math.max(position.width, 200),
              maxHeight: 300,
            }}
          >
            {/* Search input */}
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search className="size-4 text-muted-foreground shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>

            {/* Options list */}
            <div className="overflow-y-auto p-1" style={{ maxHeight: 248 }}>
              {filtered.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Aucun résultat
                </div>
              ) : (
                filtered.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground",
                      option.value === value && "bg-accent/50"
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.value === value && (
                      <span className="absolute right-2 flex size-3.5 items-center justify-center">
                        <CheckIcon className="size-4" />
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export { SearchableSelect }
export type { SearchableSelectProps }
